import React, { useState, useEffect } from 'react';
import PhoneInput from '../components/Form/PhoneInput';
import Input from '../components/Form/Input';
import { useAppSelector } from '../store/hooks';
import { useToast } from '../components/Toast/ToastContext';
import ProfileService, { type NotificationSettings as ApiNotificationSettings } from '../services/ProfileService';
import ProfileUploadModal from '../components/Settings/ProfileUploadModal';
import LocationSearchField from '../components/Settings/LocationSearchField';
import { searchLocations, type LocationSearchResult } from '../services/locationSearchService';

interface PersonalInfo {
  profilePicture: string;
  displayName: string;
  email: string;
  fullName: string;
  contact: string;
  location: string;
}

interface NotificationSettings {
  newStudentRequests: boolean;
  coachTargetWarnings: boolean;
  studentTargetWarnings: boolean;
  sessionCompletion: boolean;
}

const SettingsPage: React.FC = () => {
  const { currentUser } = useAppSelector((state) => state.adminUser);
  const { showToast } = useToast();

  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    profilePicture: currentUser?.avatar || '',
    displayName: currentUser?.name || 'Admin User',
    email: currentUser?.email || '',
    fullName: currentUser?.name || '',
    contact: currentUser?.phone || '',
    location: currentUser?.location || '',
  });

  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isNotificationSaving, setIsNotificationSaving] = useState(false);
  const [apiNotificationSettings, setApiNotificationSettings] = useState<ApiNotificationSettings | null>(null);
  
  // Location search state
  const [locationResults, setLocationResults] = useState<LocationSearchResult[]>([]);
  const [isLocationSearching, setIsLocationSearching] = useState(false);
  
  const [showDisableModal, setShowDisableModal] = useState<{
    show: boolean;
    field: keyof NotificationSettings | null;
  }>({ show: false, field: null });

  useEffect(() => {
    if (currentUser) {
      setPersonalInfo({
        profilePicture: currentUser.avatar || '',
        displayName: currentUser.name || 'Admin User',
        email: currentUser.email || '',
        fullName: currentUser.name || '',
        contact: currentUser.phone || '',
        location: currentUser.location || '',
      });
    }
  }, [currentUser]);

  // Debounced location search effect
  useEffect(() => {
    if (editingField !== 'location') {
      // Reset search when not editing location
      setLocationResults([]);
      setIsLocationSearching(false);
      return;
    }

    const query = tempValue.trim();
    if (!query) {
      setLocationResults([]);
      setIsLocationSearching(false);
      return;
    }

    setIsLocationSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchLocations(query);
        setLocationResults(results);
      } catch (error) {
        console.error('Location search error:', error);
        setLocationResults([]);
      } finally {
        setIsLocationSearching(false);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [editingField, tempValue]);

  const [autoApproval, setAutoApproval] = useState(false);
  const [notifications, setNotifications] = useState<NotificationSettings>({
    newStudentRequests: true,
    coachTargetWarnings: true,
    studentTargetWarnings: true,
    sessionCompletion: false,
  });

  const mapApiToUi = (settings: ApiNotificationSettings): NotificationSettings => ({
    newStudentRequests: settings.email_match_alert ?? true,
    coachTargetWarnings: settings.email_platform_announcement ?? true,
    studentTargetWarnings: settings.email_rescheduling_request ?? true,
    sessionCompletion: settings.email_upcoming_session_reminder ?? false,
  });

  const saveNotificationSettings = async (
    nextNotifications?: NotificationSettings,
    nextAutoApproval?: boolean
  ) => {
    if (isNotificationSaving) {
      return;
    }

    setIsNotificationSaving(true);
    try {
      const current = apiNotificationSettings || {};
      const payload: ApiNotificationSettings = {
        ...current,
        email_match_alert: nextNotifications?.newStudentRequests ?? notifications.newStudentRequests,
        email_platform_announcement: nextNotifications?.coachTargetWarnings ?? notifications.coachTargetWarnings,
        email_rescheduling_request: nextNotifications?.studentTargetWarnings ?? notifications.studentTargetWarnings,
        email_upcoming_session_reminder: nextNotifications?.sessionCompletion ?? notifications.sessionCompletion,
        ai_auto_approval_enabled: typeof nextAutoApproval === 'boolean' ? nextAutoApproval : autoApproval,
      };

      const updated = await ProfileService.updateNotificationSettings(payload);
      setApiNotificationSettings(updated);
    } catch (error: any) {
      showToast(error.message || 'Failed to update notification settings', 'error');
    } finally {
      setIsNotificationSaving(false);
    }
  };

  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        const settings = await ProfileService.getNotificationSettings();
        setApiNotificationSettings(settings);
        setNotifications(mapApiToUi(settings));
        setAutoApproval(!!settings.ai_auto_approval_enabled);
      } catch (error: any) {
        showToast(error.message || 'Failed to load notification settings', 'error');
      }
    };

    if (currentUser?.id) {
      loadNotificationSettings();
    }
  }, [currentUser?.id, showToast]);

  const handleEditClick = (field: keyof PersonalInfo) => {
    setEditingField(field);
    setTempValue(personalInfo[field]);
  };

  const handleSaveEdit = async () => {
    if (!editingField) return;

    setIsSaving(true);
    try {
      // Build the update payload - backend requires name and email always
      const updatePayload: any = {
        name: currentUser?.name || personalInfo.fullName,
        email: currentUser?.email || personalInfo.email,
      };
      
      switch (editingField) {
        case 'fullName':
          updatePayload.name = tempValue;
          break;
        case 'contact':
          updatePayload.phone = tempValue;
          break;
        case 'location':
          updatePayload.location = tempValue;
          break;
        default:
          break;
      }

      // Call the API to update the profile
       if (!currentUser?.id) {
         throw new Error('Missing user id');
       }
       await ProfileService.updateAdminProfile(currentUser.id, updatePayload);
      // Update local state on success
      setPersonalInfo({
        ...personalInfo,
        [editingField]: tempValue,
      });

      // Show success toast
      showToast(`${editingField === 'fullName' ? 'Name' : editingField === 'contact' ? 'Phone' : 'Location'} updated successfully`, 'success');
      
      setEditingField(null);
      setTempValue('');
    } catch (error: any) {
      console.error('Profile update error:', error);
      showToast(error.message || 'Failed to update profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleUploadModalSave = async (file: File) => {
    setIsSaving(true);
    setUploadError('');
    try {
      // Upload the file
      const imageUrl = await ProfileService.uploadProfilePicture(file);
      
      // Update profile with new image URL - backend requires name and email
      if (!currentUser?.id) {
        throw new Error('Missing user id');
      }
      await ProfileService.updateAdminProfile(currentUser.id, { 
        name: currentUser.name || personalInfo.fullName,
        email: currentUser.email || personalInfo.email,
        avatar: imageUrl 
      });

      // Update local state
      setPersonalInfo({
        ...personalInfo,
        profilePicture: imageUrl,
      });

      showToast('Profile picture updated successfully', 'success');
      setIsUploadModalOpen(false);
    } catch (error: any) {
      console.error('Profile picture update error:', error);
      setUploadError(error.message || 'Failed to upload profile picture');
      showToast(error.message || 'Failed to update profile picture', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotificationToggle = (field: keyof NotificationSettings) => {
    if (notifications[field]) {
      // If turning off, show confirmation modal
      setShowDisableModal({ show: true, field });
    } else {
      // If turning on, just toggle
      const next = {
        ...notifications,
        [field]: true,
      };
      setNotifications(next);
      saveNotificationSettings(next);
    }
  };

  const handleConfirmDisable = () => {
    if (showDisableModal.field) {
      const next = {
        ...notifications,
        [showDisableModal.field]: false,
      };
      setNotifications(next);
      saveNotificationSettings(next);
    }
    setShowDisableModal({ show: false, field: null });
  };

  const handleCancelDisable = () => {
    setShowDisableModal({ show: false, field: null });
  };

  const handleAutoApprovalToggle = () => {
    const next = !autoApproval;
    setAutoApproval(next);
    saveNotificationSettings(undefined, next);
  };

  return (
    <div className="space-y-8">
      {/* Personal Info Section */}
      <div className="rounded-xl border border-[#F2F4F7] bg-white shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <div className="sticky top-0 z-10 border-b border-[#F2F4F7] bg-white px-6 py-4 rounded-t-xl">
          <h2 className="text-base font-semibold text-gray-900">Personal info</h2>
        </div>

        <div className="space-y-6 p-6">
          {/* Profile Picture */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`h-16 w-16 overflow-hidden rounded-full bg-gray-100 border-1 border-gray-300 `}>
                {personalInfo.profilePicture ? (
                  <img
                    src={personalInfo.profilePicture}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-white">
                    <span className="text-2xl font-semibold">
                      {personalInfo.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-gray-900">{personalInfo.displayName}</span>
                {currentUser?.role && (
                  <span className="text-sm text-gray-500 capitalize">{currentUser.role}</span>
                )}
              </div>
            </div>
            <button 
              onClick={() => setIsUploadModalOpen(true)}
              disabled={isSaving}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Uploading...' : 'Edit Profile Picture'}
            </button>
          </div>

          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-900">Email</label>
            <p className="text-sm text-gray-500">{personalInfo.email}</p>
          </div>

          {/* Full Name */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-900">Full Name</label>
              {editingField === 'fullName' ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 max-w-xs">
                    <Input
                      value={tempValue}
                      onChange={setTempValue}
                      placeholder="Enter your full name"
                      disabled={isSaving}
                    />
                  </div>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">{personalInfo.fullName}</p>
              )}
            </div>
            {editingField !== 'fullName' && (
              <button
                onClick={() => handleEditClick('fullName')}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
              >
                Edit Name
              </button>
            )}
          </div>

          {/* Contact */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-900">Contact</label>
              {editingField === 'contact' ? (
                <div className="flex items-center gap-2">
                  <div className="max-w-xs">
                    <PhoneInput
                      value={tempValue}
                      onChange={setTempValue}
                      placeholder="Enter phone number"
                      disabled={isSaving}
                    />
                  </div>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">{personalInfo.contact}</p>
              )}
            </div>
            {editingField !== 'contact' && (
              <button
                onClick={() => handleEditClick('contact')}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
              >
                Edit Contact
              </button>
            )}
          </div>

          {/* Location */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-900">Location</label>
              {editingField === 'location' ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 max-w-md">
                    <LocationSearchField
                      value={tempValue}
                      options={locationResults}
                      placeholder="Search for location"
                      onChange={setTempValue}
                      disabled={isSaving}
                      loading={isLocationSearching}
                    />
                  </div>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">{personalInfo.location}</p>
              )}
            </div>
            {editingField !== 'location' && (
              <button
                onClick={() => handleEditClick('location')}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
              >
                Edit Location
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Student Requests Section */}
      <div className="rounded-xl border border-[#F2F4F7] bg-white shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <div className="border-b border-[#F2F4F7] px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Student Requests</h2>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">
                Enable Auto-Approval of AI Suggestions
              </h3>
              <p className="mt-1 text-sm text-gray-400">
                When enabled, AI suggestions are automatically assigned without manual review
              </p>
            </div>
            <button
              onClick={handleAutoApprovalToggle}
              className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                autoApproval ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoApproval ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Email Notifications Section */}
      <div className="rounded-xl border border-[#F2F4F7] bg-white shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
        <div className="border-b border-[#F2F4F7] px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Email Notifications</h2>
        </div>

        <div className="divide-y divide-[#F2F4F7]">
          {/* New student requests */}
          <div className="flex items-start justify-between px-6 py-5">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">New student requests</h3>
              <p className="mt-1 text-sm text-gray-400">
                Receive an email whenever a new student sends request for a coach.
              </p>
            </div>
            <button
              onClick={() => handleNotificationToggle('newStudentRequests')}
              className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                notifications.newStudentRequests ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.newStudentRequests ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Coach target warnings */}
          <div className="flex items-start justify-between px-6 py-5">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">Coach target warnings</h3>
              <p className="mt-1 text-sm text-gray-400">
                Receive an email whenever a coach is about to complete their target.
              </p>
            </div>
            <button
              onClick={() => handleNotificationToggle('coachTargetWarnings')}
              className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                notifications.coachTargetWarnings ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.coachTargetWarnings ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Student target warnings */}
          <div className="flex items-start justify-between px-6 py-5">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">Student target warnings</h3>
              <p className="mt-1 text-sm text-gray-400">
                Receive an email whenever a student is about to complete their target.
              </p>
            </div>
            <button
              onClick={() => handleNotificationToggle('studentTargetWarnings')}
              className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                notifications.studentTargetWarnings ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.studentTargetWarnings ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Session completion */}
          <div className="flex items-start justify-between px-6 py-5">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">Session completion</h3>
              <p className="mt-1 text-sm text-gray-400">
                Receive an email whenever a session is completed.
              </p>
            </div>
            <button
              onClick={() => handleNotificationToggle('sessionCompletion')}
              className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                notifications.sessionCompletion ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications.sessionCompletion ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Disable Confirmation Modal */}
      {showDisableModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <h3 className="mb-2 text-center text-lg font-semibold text-gray-900">
              Disable Email Notification?
            </h3>
            <p className="mb-6 text-center text-sm text-gray-600">
              You won't get any email whenever a new student sends request for a coach.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelDisable}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDisable}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Upload Modal */}
      <ProfileUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setUploadError('');
        }}
        onSave={handleUploadModalSave}
        isSaving={isSaving}
        error={uploadError}
      />
    </div>
  );
};

export default SettingsPage;
